import { subsolarPoint } from '../features/world/sun';

/**
 * Is it Shabbat *there* right now? Shabbat begins on Friday at candle
 * lighting (18 minutes before sunset) and ends on Saturday at nightfall (the
 * sun 8.5° below the horizon). Both depend on where the person is — their
 * latitude and longitude for the sun, their time zone for which day it is —
 * so someone in Jerusalem lights candles hours before a cousin in Toronto.
 *
 * Pure maths, no DOM: the sun's position comes from the same low-precision
 * solar model that draws the globe's day/night line (good to a few minutes,
 * plenty for an indicator).
 */

export interface ShabbatStatus {
  /** Shabbat right now, at this place. */
  on: boolean;
  /** When the current Shabbat began (when `on`) or the next one begins. */
  begins: Date;
  /** When the current / next Shabbat ends. */
  ends: Date;
}

const RAD = Math.PI / 180;
const MINUTE = 60_000;
const DAY = 86_400_000;
/** Candle lighting: minutes before sunset. */
export const CANDLE_LIGHTING_MIN = 18;
/** Nightfall for the end of Shabbat: the sun this far below the horizon (degrees). */
export const NIGHTFALL_DEPRESSION = 8.5;
/** Sunset: the sun's centre this far below the horizon (refraction + the sun's radius). */
const SUNSET_DEPRESSION = 0.833;

/** Weekday (0 = Sunday … 6 = Saturday) and YYYY-MM-DD of an instant in a time zone. */
export function localDayIn(now: Date, timezone?: string): { weekday: number; date: string } {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    return { weekday: weekday < 0 ? now.getDay() : weekday, date: `${get('year')}-${get('month')}-${get('day')}` };
  } catch {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return { weekday: now.getDay(), date: `${y}-${m}-${d}` };
  }
}

/** Offset of a time zone from UTC, in minutes, at a given instant. */
export function tzOffsetMinutes(at: Date, timezone?: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(at);
    const v = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    const asUtc = Date.UTC(v('year'), v('month') - 1, v('day'), v('hour') % 24, v('minute'), v('second'));
    return Math.round((asUtc - at.getTime()) / MINUTE);
  } catch {
    return -at.getTimezoneOffset();
  }
}

/** The instant of local midnight starting `date` (YYYY-MM-DD) in a time zone. */
export function localMidnight(date: string, timezone?: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d);
  // Two passes cope with the offset changing (DST) between the guess and the answer.
  let t = guess - tzOffsetMinutes(new Date(guess), timezone) * MINUTE;
  t = guess - tzOffsetMinutes(new Date(t), timezone) * MINUTE;
  return new Date(t);
}

/**
 * When the sun's centre passes `depression` degrees below the horizon on its
 * way down, for the local day starting at `dayStart` (an instant), at a place.
 * Undefined in polar day / night, when the sun never gets there.
 */
export function sunsetAt(lat: number, lng: number, dayStart: Date, depression = SUNSET_DEPRESSION): Date | undefined {
  // Solar noon: when the sub-solar point is on this meridian. Start from local noon and
  // correct a few times using the real sun (its longitude sweeps westward 15° per hour).
  let noon = dayStart.getTime() + DAY / 2;
  for (let i = 0; i < 3; i++) {
    const sun = subsolarPoint(new Date(noon));
    let dLng = sun.lng - lng;
    if (dLng > 180) dLng -= 360;
    if (dLng < -180) dLng += 360;
    noon += (dLng / 15) * 3_600_000;
  }
  const dec = subsolarPoint(new Date(noon)).lat * RAD;
  const φ = lat * RAD;
  const cosH = (Math.sin(-depression * RAD) - Math.sin(φ) * Math.sin(dec)) / (Math.cos(φ) * Math.cos(dec));
  if (cosH < -1 || cosH > 1) return undefined;
  const hours = (Math.acos(cosH) / RAD) / 15;
  return new Date(noon + hours * 3_600_000);
}

/** Fallback when the sun never sets or rises there (far north / south): fixed local hours. */
const FALLBACK_BEGIN_H = 18;
const FALLBACK_END_H = 20;

/**
 * Shabbat status at a place: the current Shabbat's bounds when it is on, else
 * the coming one's. `timezone` decides the calendar day; latitude/longitude
 * decide sunset. Without coordinates the fixed fallback hours are used.
 */
export function shabbatAt(lat: number | undefined, lng: number | undefined, timezone: string | undefined, now: Date = new Date()): ShabbatStatus {
  const { weekday, date } = localDayIn(now, timezone);
  // Days since the most recent Friday (0 on Friday itself, 1 on Saturday, 6 on Thursday).
  const sinceFriday = (weekday - 5 + 7) % 7;
  const midnightToday = localMidnight(date, timezone);
  const bounds = (fridayMidnight: Date) => {
    const saturdayMidnight = new Date(fridayMidnight.getTime() + DAY);
    const hasSun = lat !== undefined && lng !== undefined;
    const sunsetFri = hasSun ? sunsetAt(lat, lng, fridayMidnight) : undefined;
    const nightfallSat = hasSun ? sunsetAt(lat, lng, saturdayMidnight, NIGHTFALL_DEPRESSION) : undefined;
    const begins = sunsetFri ? new Date(sunsetFri.getTime() - CANDLE_LIGHTING_MIN * MINUTE) : new Date(fridayMidnight.getTime() + FALLBACK_BEGIN_H * 3_600_000);
    const ends = nightfallSat ?? new Date(saturdayMidnight.getTime() + FALLBACK_END_H * 3_600_000);
    return { begins, ends };
  };
  // The most recent Friday's Shabbat: still on, already over, or not yet begun?
  const lastFriday = new Date(midnightToday.getTime() - sinceFriday * DAY);
  const current = bounds(lastFriday);
  if (now >= current.begins && now < current.ends) return { on: true, ...current };
  if (now < current.begins) return { on: false, ...current };
  const next = bounds(new Date(lastFriday.getTime() + 7 * DAY));
  return { on: false, ...next };
}

/** True when it is Shabbat at the place right now. */
export function isShabbatAt(lat: number | undefined, lng: number | undefined, timezone: string | undefined, now: Date = new Date()): boolean {
  return shabbatAt(lat, lng, timezone, now).on;
}
